import React from 'react';
import { CardGrid } from '@naf/card-grid';
import { AdvancedImage, lazyload } from '@cloudinary/react';
import { useCloudinary } from '../../../hooks/Cloudinary/useCloudinary';
import { StyledLink } from '../../styled-link/StyledLink';
import { ArticleType } from '../../../../../types/articleType';
import { createNorwegianDateString } from '../../../utils/createNorwegianDateString';
import { DocumentTypeLabel } from '../../../../../types/testType';
import defaultImage from '../../../../assets/images/defaultLocalArticle.svg?url';
import { useDocumentUrlWithNode } from '../../../hooks/Documents/useDocumentUrl';
import { CardGridWrapper } from '../ArticleGrid.styles';

interface Props {
  article: ArticleType;
  showDate?: boolean;
  showNewsLabel?: boolean;
  isLcpCandidate?: boolean;
}

export const ArticleItemCard = ({
  article,
  showDate = false,
  showNewsLabel = false,
  isLcpCandidate = false,
}: Props) => {
  const {
    image,
    name,
    category,
    ingress,
    meta,
    metaDisplay,
    type,
    localArticleImage,
    paywallDetails,
    abTestExperimentSlug,
  } = article;

  const imageAlt = image?.altText || category?.image?.altText;

  // Link to document
  const documentLink = useDocumentUrlWithNode(article);

  // Check whether the documentLink is overwritten by the document being part of an experiment
  // If it is, route the link via the experiment it is part of.
  const link = abTestExperimentSlug ? `/${abTestExperimentSlug}` : documentLink;

  const { getProcessedImage } = useCloudinary();
  const publishedDate = createNorwegianDateString(new Date(meta?.publishAt));
  const itemImage = getProcessedImage(image?.publicId, {
    height: 288,
    width: 512,
    focusMethod: image?.focus,
    quality: 'auto:good',
  });

  const localImage = localArticleImage?.source === 'NafIllustration' ? defaultImage : localArticleImage?.url || '';

  return (
    <CardGridWrapper s="12" m="12" l="4" xl="4" key={`article-item-${name}`}>
      <StyledLink to={link}>
        <CardGrid
          headerTag="h2"
          title={name}
          img={{
            src: localImage,
            alt: localArticleImage?.caption || '',
          }}
          advancedImage={
            itemImage && (
              <AdvancedImage
                alt={imageAlt || ''}
                cldImg={itemImage}
                // Lead card is the LCP element once the list is server-rendered.
                fetchPriority={isLcpCandidate ? 'high' : undefined}
                // The lazyload plugin is a no-op server-side, so React preloaded all nine cards.
                // Only the attribute suppresses that; the plugin still covers the client-rendered
                // pages, where AdvancedImage emits no `src` until it resolves.
                loading={isLcpCandidate ? undefined : 'lazy'}
                plugins={
                  isLcpCandidate ? undefined : [lazyload({ rootMargin: '10px 20px 10px 30px', threshold: 0.25 })]
                }
              />
            )
          }
          date={showDate && !metaDisplay?.hidePublicationDate ? publishedDate : ''}
          label={
            showNewsLabel && type === DocumentTypeLabel.nafnoNewsArticle
              ? {
                  text: 'NYHET',
                  size: 'small',
                  variant: 'signature',
                }
              : undefined
          }
          memberLabel={
            paywallDetails
              ? {
                  labelText: paywallDetails?.labelText || 'For medlemmer',
                  inverted: true,
                  isEligible: paywallDetails?.isAccessibleForUser,
                }
              : undefined
          }
        >
          {ingress}
        </CardGrid>
      </StyledLink>
    </CardGridWrapper>
  );
};
